import {
  onCreateDraftNotificationSuccess,
  onCreateNotificationSuccess,
} from 'app/auth/store/actions/createNotification.actions'
import MessageAlert from 'app/components/MessageAlert'
import { TYPE_VOUCHER } from 'app/constants/messageType'
import effects from 'app/effects/effects'
import { errorTypes, networkErrorToMessage } from 'commons/error'
import { getLangaugeId, useDefaultLanguage } from 'commons/useDefaultLanguage'
import { usePlace } from 'commons/usePlace'
import { removeBase64Images } from 'libs/base64'
import { isEmptyOrNil } from 'libs/empty'
import {
  defaultTo,
  filter,
  isEmpty,
  isNil,
  lensProp,
  map,
  over,
  pickAll,
  prop,
  propEq,
} from 'ramda'
import { useCallback, useEffect, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useFlag } from '@unleash/proxy-client-react'
import {
  MESSAGE_CREDIT_NOT_ENOUGH_CODE,
  SEND_MESSAGE_PERMISSION_DENIED,
} from '../notification/messageCreditAlertDialog/utils'
import { mapBeaconToApi } from './beaconMapping'
import { mapToApi } from './mapping'
import { normalizeEmptyParagraphs } from './tiptapEditor/TiptapUtils'

const OVERRIDE_TRANSLATION = 'overrideTranslation'

const TYPE_LOADING = 'loading'
const TYPE_SUCCESS = 'success'
const TYPE_ERROR = 'error'
const TYPE_INIT = 'initial'

const initialState = {
  isLoading: false,
  values: undefined,
  errors: undefined,
  notificationType: undefined,
}
const pickAllTranslated = pickAll(['title', 'body'])

const pickVoucherTranslated = pickAll([
  'title',
  'body',
  'description',
  'voucherTitle',
  'voucherDescription',
])

const createTranslationPayload = (languageId) => (content) => ({
  languageId: languageId,
  ...(content.notiType === TYPE_VOUCHER
    ? pickVoucherTranslated(content)
    : pickAllTranslated(content)),
})

const submitReducer = (state, action) => {
  switch (action.type) {
    case TYPE_LOADING:
      return {
        ...state,
        isLoading: true,
        values: undefined,
        errors: undefined,
      }
    case TYPE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        values: action.values,
        errors: undefined,
        isSuccess: true,
      }
    case TYPE_ERROR:
      return {
        ...state,
        isLoading: false,
        values: undefined,
        errors: action.errors,
        notificationType: action.notificationType,
      }
    case TYPE_INIT:
      return initialState
    default:
      return state
  }
}

export default (onCreateSuccess, editId, isDraft, requestId, isCopyMessage) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const language = useDefaultLanguage()
  const defaultLanguageId = getLangaugeId(language)
  const place = usePlace()
  const [submitState, submitDispatch] = useReducer(submitReducer, initialState)
  const mes_5281_save_draft_for_polls = useFlag('mes_5281_save_draft_for_polls')

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (submitState.errors) {
      const messageCreditNotEnoughErrorCodeFromAPI =
        submitState?.errors?.response?.data?.details?.code
      if (
        messageCreditNotEnoughErrorCodeFromAPI ===
        SEND_MESSAGE_PERMISSION_DENIED
      ) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.permission.denied' }),
          }),
        )
      } else if (
        messageCreditNotEnoughErrorCodeFromAPI !==
        MESSAGE_CREDIT_NOT_ENOUGH_CODE
      ) {
        dispatch(
          MessageAlert.showError(
            networkErrorToMessage(formatMessage)(
              submitState.errors,
              submitState.notificationType === TYPE_VOUCHER
                ? errorTypes.voucher
                : null,
            ),
          ),
        )
      }
    }
  }, [dispatch, submitState.errors])
  /* eslint-enable react-hooks/exhaustive-deps */

  const translateAndSubmit = useCallback(
    (submitModel) => {
      submitDispatch({ type: TYPE_LOADING })
      const overrideTranslation = prop(OVERRIDE_TRANSLATION)(submitModel)

      const getDefaultLanguageList = () => {
        const hasDefaultInOverrideTranslation = !isEmpty(
          filter(
            propEq('languageId', defaultLanguageId),
            overrideTranslation || [],
          ),
        )
        if (hasDefaultInOverrideTranslation) {
          return overrideTranslation
        } else {
          return [createTranslationPayload(defaultLanguageId)(submitModel)]
        }
      }
      const defaultLanguageList = getDefaultLanguageList()
      if (defaultLanguageList && defaultLanguageList[0]) {
        delete defaultLanguageList[0]['languageId']
      }

      const translations = map(
        over(
          lensProp('body'),
          (body) => normalizeEmptyParagraphs(removeBase64Images(body)) ?? '',
        ),
        defaultTo(defaultLanguageList, overrideTranslation),
      )

      return translations
    },
    [defaultLanguageId],
  )

  const sendNotification = useCallback(
    (notification) => {
      const timezone = isNil(notification.eventStartDateTime)
        ? place.info.timezone
        : notification.eventTimezone
      const mapTimezoneModel = {
        ...notification,
        timezone,
      }

      return effects.apis
        .sendNotification(notification)
        .then((resp) => {
          submitDispatch({ type: TYPE_SUCCESS, values: resp })
          dispatch(onCreateNotificationSuccess())
          onCreateSuccess(resp, notification.scope)
        })
        .catch((errors) => {
          submitDispatch({
            type: TYPE_ERROR,
            errors,
            notificationType: mapTimezoneModel.notiType,
          })
        })
    },
    [dispatch, onCreateSuccess, place.info.timezone],
  )

  const sendDraftNotification = useCallback(
    (draftId, notification) => {
      const timezone = isNil(notification.eventStartDateTime)
        ? place.info.timezone
        : notification.eventTimezone
      const mapTimezoneModel = {
        ...notification,
        timezone,
      }

      return effects.apis
        .sendDraftNotification(draftId, notification)
        .then((resp) => {
          submitDispatch({ type: TYPE_SUCCESS, values: resp })
          dispatch(onCreateNotificationSuccess())
          onCreateSuccess(resp, notification.scope)
        })
        .catch((errors) => {
          submitDispatch({
            type: TYPE_ERROR,
            errors,
            notificationType: mapTimezoneModel.notiType,
          })
        })
    },
    [dispatch, onCreateSuccess, place.info.timezone],
  )

  const updateNotification = useCallback(
    (notification) => {
      const timezone = isNil(notification.eventStartDateTime)
        ? place.info.timezone
        : notification.eventTimezone
      const mapTimezoneModel = { ...notification, timezone }
      return effects.apis
        .updateNotification(editId, notification)
        .then(() =>
          effects.apis.getNotificationById(editId).then((response) => {
            submitDispatch({ type: TYPE_SUCCESS, values: response })
            dispatch(onCreateNotificationSuccess())
            onCreateSuccess(editId, notification.scope)
          }),
        )
        .catch((errors) =>
          submitDispatch({
            type: TYPE_ERROR,
            errors,
            notificationType: mapTimezoneModel.notiType,
          }),
        )
    },
    [dispatch, editId, onCreateSuccess, place.info.timezone],
  )

  const submitNoti = useCallback(
    (submitModel, isUserAcceptOverdraft = false) => {
      const modelMapValue = { ...submitModel, requestId }
      const translations = translateAndSubmit(modelMapValue)
      const isBeaconDialog = modelMapValue.isBeaconDialog

      const notification = isBeaconDialog
        ? mapBeaconToApi(modelMapValue)(translations)
        : mapToApi(modelMapValue, {
            mes_5281_save_draft_for_polls,
            isDraft: false,
            draftId: isCopyMessage ? undefined : editId,
          })(translations)

      if (isDraft && !mes_5281_save_draft_for_polls) {
        // When feature flag is off, use old draft send endpoint
        sendDraftNotification(editId, {
          ...notification,
          isUserAcceptOverdraft,
        })
      } else if (isDraft || isEmptyOrNil(editId) || isCopyMessage) {
        // When feature flag is on or new message, use regular send endpoint
        sendNotification({
          ...notification,
          isUserAcceptOverdraft,
        })
      } else {
        updateNotification(notification)
      }
    },
    [
      editId,
      isDraft,
      translateAndSubmit,
      sendNotification,
      sendDraftNotification,
      updateNotification,
      requestId,
      isCopyMessage,
      mes_5281_save_draft_for_polls,
    ],
  )

  const submitDraft = useCallback(
    (submitModel) => {
      const timezone = isNil(submitModel.eventStartDateTime)
        ? place.info.timezone
        : submitModel.eventTimezone

      const modelMapValue = { ...submitModel, timezone, requestId }
      const enableSavePollAsDraft = mes_5281_save_draft_for_polls

      const translations = translateAndSubmit(modelMapValue)
      const apiEffect = async (notification) => {
        const isNewDraft = isEmptyOrNil(editId) || isCopyMessage

        if (enableSavePollAsDraft) {
          return isNewDraft
            ? effects.apis.saveMessageAsDraft({ data: notification })
            : effects.apis.editDraftMessage({ id: editId, data: notification })
        }

        return isNewDraft
          ? effects.apis.createDraftNotification(notification)
          : effects.apis.updateDraftNotification({
              notificationId: editId,
              notification,
            })
      }

      apiEffect(
        mapToApi(modelMapValue, {
          mes_5281_save_draft_for_polls,
          isDraft: true,
        })(translations),
      )
        .then((resp) => {
          submitDispatch({ type: TYPE_SUCCESS, values: resp })
          dispatch(onCreateDraftNotificationSuccess())
          onCreateSuccess(resp)
          dispatch(
            MessageAlert.showSuccess({
              message: formatMessage({ id: 'draft.success.message' }),
            }),
          )
        })
        .catch((errors) =>
          submitDispatch({
            type: TYPE_ERROR,
            errors,
            notificationType: modelMapValue.notiType,
          }),
        )
    },
    [
      dispatch,
      editId,
      formatMessage,
      onCreateSuccess,
      place.info.timezone,
      translateAndSubmit,
      requestId,
      mes_5281_save_draft_for_polls,
    ],
  )

  return {
    submitNoti,
    submitDraft,
    isSubmitting: submitState.isLoading,
    submitErrorDetails: submitState?.errors?.response?.data?.details || null,
  }
}
